import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Bell, Eye, Send, Users, TrendingUp, PieChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface NotificationStats {
  total: number;
  read: number;
  unread: number;
  readRate: number;
}

interface TypeBreakdown {
  type: string;
  count: number;
}

interface DailyTrend {
  date: string;
  sent: number;
  read: number;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--muted))', 'hsl(var(--accent))', 'hsl(var(--secondary))'];

export default function AdminNotificationAnalytics() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7');
  const [stats, setStats] = useState<NotificationStats>({ total: 0, read: 0, unread: 0, readRate: 0 });
  const [typeBreakdown, setTypeBreakdown] = useState<TypeBreakdown[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);
  const [broadcastStats, setBroadcastStats] = useState({ total: 0, totalSent: 0 });
  const [pushStats, setPushStats] = useState({ totalSubscriptions: 0, activeUsers: 0 });

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const days = parseInt(timeRange);
      const startDate = startOfDay(subDays(new Date(), days));
      const endDate = endOfDay(new Date());

      // Fetch notification stats
      const { data: notifications, error: notifError } = await supabase
        .from('notifications')
        .select('id, read, type, created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (notifError) throw notifError;

      const total = notifications?.length || 0;
      const read = notifications?.filter(n => n.read).length || 0;
      const unread = total - read;
      const readRate = total > 0 ? Math.round((read / total) * 100) : 0;

      setStats({ total, read, unread, readRate });

      // Type breakdown
      const typeMap = new Map<string, number>();
      notifications?.forEach(n => {
        const count = typeMap.get(n.type) || 0;
        typeMap.set(n.type, count + 1);
      });
      
      const breakdown = Array.from(typeMap.entries())
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
      setTypeBreakdown(breakdown);

      // Daily trend
      const dailyMap = new Map<string, { sent: number; read: number }>();
      for (let i = days; i >= 0; i--) {
        const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
        dailyMap.set(date, { sent: 0, read: 0 });
      }

      notifications?.forEach(n => {
        const date = format(new Date(n.created_at), 'yyyy-MM-dd');
        const current = dailyMap.get(date) || { sent: 0, read: 0 };
        current.sent++;
        if (n.read) current.read++;
        dailyMap.set(date, current);
      });

      const trend = Array.from(dailyMap.entries()).map(([date, data]) => ({
        date: format(new Date(date), 'MMM d'),
        sent: data.sent,
        read: data.read,
      }));
      setDailyTrend(trend);

      // Broadcast stats
      const { data: broadcasts } = await supabase
        .from('broadcast_notifications')
        .select('id, sent_count')
        .gte('created_at', startDate.toISOString());

      const totalBroadcasts = broadcasts?.length || 0;
      const totalSent = broadcasts?.reduce((sum, b) => sum + b.sent_count, 0) || 0;
      setBroadcastStats({ total: totalBroadcasts, totalSent });

      // Push subscription stats
      const { count: subscriptionCount } = await supabase
        .from('push_subscriptions')
        .select('*', { count: 'exact', head: true });

      const { data: uniqueUsers } = await supabase
        .from('push_subscriptions')
        .select('user_id');

      const uniqueUserCount = new Set(uniqueUsers?.map(u => u.user_id)).size;
      setPushStats({ 
        totalSubscriptions: subscriptionCount || 0, 
        activeUsers: uniqueUserCount 
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTypeName = (type: string) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification Analytics</h1>
          <p className="text-muted-foreground">Track notification delivery and engagement</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">notifications in period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Read Rate</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.readRate}%</div>
            <p className="text-xs text-muted-foreground">
              {stats.read.toLocaleString()} read / {stats.unread.toLocaleString()} unread
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Broadcasts</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{broadcastStats.total}</div>
            <p className="text-xs text-muted-foreground">
              {broadcastStats.totalSent.toLocaleString()} total recipients
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Push Subscriptions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pushStats.totalSubscriptions}</div>
            <p className="text-xs text-muted-foreground">
              {pushStats.activeUsers} unique users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Daily Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Notification Trend
            </CardTitle>
            <CardDescription>Daily sent vs read notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))' 
                    }} 
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sent" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    name="Sent"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="read" 
                    stroke="hsl(var(--accent))" 
                    strokeWidth={2}
                    name="Read"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Type Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              By Type
            </CardTitle>
            <CardDescription>Notification distribution by type</CardDescription>
          </CardHeader>
          <CardContent>
            {typeBreakdown.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={typeBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="type"
                      label={({ type, count }) => `${formatTypeName(type)}: ${count}`}
                    >
                      {typeBreakdown.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No notifications in this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Type Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Top Notification Types</CardTitle>
            <CardDescription>Most common notification categories</CardDescription>
          </CardHeader>
          <CardContent>
            {typeBreakdown.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={typeBreakdown.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis 
                      type="category" 
                      dataKey="type" 
                      className="text-xs"
                      tickFormatter={formatTypeName}
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                      formatter={(value) => [value, 'Count']}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
